import axios from 'axios';
import storeEngine from 'store/src/store-engine';
import sessionStorage from 'store/storages/sessionStorage';
import cookieStorage from 'store/storages/cookieStorage';

import {
  TRANSLATIONS_PREFIX_FRONT,
  TRANSLATIONS_PREFIX_COMMON,
  COPIES_URI,
  BACKEND_URL
} from './apiConfig';

/**
 * @description Look for the translations that match key and belongs to the section
 *
 * @param {object} translations - The filled translations object from storage
 * @param {string} key - The key of copy , only last part. ex: for `be.common.book` key = book
 * @param {string} sectionRequested - The section to which the copy belongs,
 *  if undefined will get defaultValue = `common`
 *
 * @returns {array} of translations for section-key
 */
const getTranslations = (translations, key, sectionRequested = TRANSLATIONS_PREFIX_COMMON) => {
  const result = translations.filter(
    t => t.key === `${sectionRequested}.${key}`
  );

  if (result.length < 1 && sectionRequested !== TRANSLATIONS_PREFIX_COMMON) {
    return translations.filter(
      t => t.key === `${TRANSLATIONS_PREFIX_COMMON}.${key}`
    );
  }

  return result;
};

/**
 * @description Look for the translations that match key and belongs to the section
 *
 * @param {object} translations - The filled translations object from storage
 * @param {string} key - The key of copy , only last part. ex: for `be.common.book` key = book
 * @param {string} sectionRequested - The section to which the copy belongs
 *
 * @returns {string} translation text
 */
const getSectionTranslation = (
  translations,
  section,
  lang,
  key,
  numItems = undefined,
  replacers = []
) => {
  const itemTranslations = getTranslations(
    translations[section][lang],
    key,
    section
  );

  // stop here if we cannot find translations
  if (itemTranslations.length < 1) { return false; }

  const translation = itemTranslations[0];

  let result;

  switch (parseInt(numItems, 10)) {
    case undefined:
      result = translation.textBase;
      break;
    case 0:
      result = translation.textZero.replace('{n}', numItems);
      break;
    case 1:
      result = translation.textOne.replace('{n}', numItems);
      break;
    default:
      result = translation.textBase.replace('{n}', numItems);
      break;
  }

  replacers.forEach((replacer, index) => {
    result = result.replace(`{${index}}`, replacer);
  });

  return result;
};

/**
 * @description Make a call to banck-end to get all copies of the desired section and language
 *
 * @param {string} section - The section to request all copies,
 *  if undefined will get all available copies
 *
 * @returns {array} of translations for section and language
 */
const getCopies = async (section) => {
  const headers = {
    Authorization: window.localStorage.getItem('tokenAccess')
  };

  const requestOptions = {
    method: 'GET',
    url: `${BACKEND_URL}/${COPIES_URI}?prefix=${section}`,
    headers,
    json: true
  };

  try {
    return await axios(requestOptions);
  } catch (error) {
    return error;
  }
};

/**
 * @description Initialize Storage to avoid un-necesary calls, if supported will use
 * sessionStorage, else will use cookiesStorage. It creates store object then make a
 * request to fill it is not already filled.
 *
 * @param {string} lang - The language to initalize storage
 * @param {string} section - The section which you are requesting translations
 */
const initializeTranslationStorage = async (
  lang = '', section = TRANSLATIONS_PREFIX_FRONT
) => {
  const store = storeEngine.createStore([sessionStorage, cookieStorage]);
  const copies = await getCopies(section);
  if (
    copies.data
    && Object.hasOwnProperty.call(copies.data, section)
    && Object.hasOwnProperty.call(copies.data[section], lang)
  ) {
    store.set('translations', [...copies.data[section][lang]]);
    return copies.data;
  }

  return copies;
};

/**
 * @description Translate to `currentLang` (auto-detected) the string mapped
 * to `key` passed.
 * `currentSection` (auto-detected) value have priority over
 * common section copies.
 *
 * @param {string} key - The key of copy , only last part.
 * ex: for `be.common.book` key = book
 * @param {number} numItems - The number of items, copies service will
 * return one message or * another depending of this.
 * @param {array} replacers - Array of replacers , will use arrayIndex to know
 * which {placeholder} change. So replacers[0] will be used in {0} consecutively
 *
 * @returns {string} translation text
 */
const translate = (key, replacers = undefined) => {
  const store = storeEngine.createStore([sessionStorage, cookieStorage]);

  // fill with storage data previously initalize
  const translations = store.get('translations');
  if (typeof translations === 'undefined') {
    return key;
  }

  // return currentSection.key if for some reason we cant obtain translation
  const translation = translations.find(t => t.key === key);

  if (typeof translation === 'undefined') {
    return key;
  }

  let result = translation.message;

  if (replacers && replacers.length > 0) {
    replacers.forEach((replacer, index) => {
      result = result.replace(`{${index}}`, replacer);
    });
  }

  return result;
};

export { translate, initializeTranslationStorage, getSectionTranslation };
